import { createFileRoute } from "@tanstack/react-router"
import {
	IconPhone,
	IconActivity,
} from "@tabler/icons-react"
import { AppShell } from "@/components/layout/app-shell"
import { ProtectedRoute } from "@/components/layout/protected-route"
import { Badge } from "@/components/ui/badge"
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card"
import { useMerchantSims } from "@/hooks/use-merchant-sims"

export const Route = createFileRoute("/sims")({
	component: SimsPage,
})

function SimsPage() {
	return (
		<ProtectedRoute>
			<AppShell>
				<SimsContent />
			</AppShell>
		</ProtectedRoute>
	)
}

const operatorColors: Record<string, string> = {
	bkash: "text-pink-600 bg-pink-50 border-pink-200",
	nagad: "text-orange-600 bg-orange-50 border-orange-200",
	rocket: "text-purple-600 bg-purple-50 border-purple-200",
	upay: "text-blue-600 bg-blue-50 border-blue-200",
	cellfin: "text-green-600 bg-green-50 border-green-200",
}

function SimsContent() {
	const { data: simsData, isLoading, error } = useMerchantSims()

	const sims = simsData?.sims || []
	const byOperator = simsData?.byOperator || { bkash: 0, nagad: 0, rocket: 0, upay: 0, cellfin: 0 }
	const total = simsData?.total || 0

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-3xl font-bold tracking-tight">Assigned SIMs</h1>
				<p className="text-muted-foreground">
					View all SIM cards assigned to your account
				</p>
			</div>

			{/* Summary Cards */}
			<div className="grid gap-4 md:grid-cols-4">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Total SIMs</CardTitle>
						<IconPhone className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{total}</div>
						<p className="text-xs text-muted-foreground">Assigned to your account</p>
					</CardContent>
				</Card>

				<Card className="border-pink-200">
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">bKash</CardTitle>
						<IconPhone className="h-4 w-4 text-pink-600" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{byOperator.bkash}</div>
						<p className="text-xs text-muted-foreground">Active SIMs</p>
					</CardContent>
				</Card>

				<Card className="border-orange-200">
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Nagad</CardTitle>
						<IconPhone className="h-4 w-4 text-orange-600" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{byOperator.nagad}</div>
						<p className="text-xs text-muted-foreground">Active SIMs</p>
					</CardContent>
				</Card>

				<Card className="border-purple-200">
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Rocket</CardTitle>
						<IconPhone className="h-4 w-4 text-purple-600" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{byOperator.rocket}</div>
						<p className="text-xs text-muted-foreground">Active SIMs</p>
					</CardContent>
				</Card>
			</div>

			{/* SIMs Grid */}
			{isLoading ? (
				<div className="flex items-center justify-center h-64">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
				</div>
			) : error ? (
				<div className="flex items-center justify-center h-64">
					<p className="text-destructive">Failed to load SIMs</p>
				</div>
			) : sims.length === 0 ? (
				<Card>
					<CardContent className="flex items-center justify-center h-64">
						<div className="text-center">
							<IconPhone className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
							<p className="text-muted-foreground">No SIMs assigned to your account yet</p>
						</div>
					</CardContent>
				</Card>
			) : (
				<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
					{sims.map((sim) => {
						const colorClass = operatorColors[sim.operator.toLowerCase()] || "text-gray-600 bg-gray-50 border-gray-200"

						return (
							<Card key={sim.id} className={`hover:shadow-md transition-shadow ${colorClass}`}>
								<CardHeader>
									<div className="flex items-center justify-between">
										<div className={`p-3 rounded-lg border ${colorClass}`}>
											<IconPhone className="h-5 w-5" />
										</div>
										<Badge variant={sim.status === "active" ? "default" : "secondary"}>
											{sim.status}
										</Badge>
									</div>
									<CardTitle className="mt-4">{sim.phoneNumber}</CardTitle>
									<CardDescription className="capitalize">{sim.operator}</CardDescription>
								</CardHeader>
								<CardContent className="space-y-2">
									<div className="flex items-center justify-between text-sm">
										<span className="text-muted-foreground">Balance</span>
										<span className="font-medium">৳{Number(sim.currentBalance || 0).toLocaleString()}</span>
									</div>
									{sim.lastTransactionAt && (
										<div className="flex items-center justify-between text-sm">
											<span className="text-muted-foreground">Last Transaction</span>
											<span className="font-medium">
												{new Date(sim.lastTransactionAt).toLocaleDateString()}
											</span>
										</div>
									)}
									<div className="pt-2 flex items-center text-xs text-muted-foreground">
										<IconActivity className="h-3 w-3 mr-1" />
										Active and ready to receive payments
									</div>
								</CardContent>
							</Card>
						)
					})}
				</div>
			)}
		</div>
	)
}
